import { AdSet } from '../../entities/ad-set';
import { Npi } from '../../entities/npi';
import { NpiSourceInterface } from '../../interfaces/components/npi/npi-source.interface';
import { PublicNpi } from '../../types';
import { UserContext } from '../context/user.context';
import { Injectable, MultiInject, Optional } from '../dependency-injection/decorators';
import { Events } from '../event-manager/event/events';
import { NpiPostDetectEvent } from '../event-manager/event/npi-post-detect.event';
import { NpiPreDetectEvent } from '../event-manager/event/npi-pre-detect.event';
import { EventManager } from '../event-manager/event-manager';
import { Logger } from '../logger/logger';

@Injectable()
export class NpiDetector {
  public constructor (
    @MultiInject('NpiSource') private readonly sources: NpiSourceInterface[],
    private readonly userContext: UserContext,
    private readonly eventManager: EventManager,
    @Optional() private readonly logger?: Logger,
  ) {
  }

  public async detect (adSet: AdSet): Promise<void> {
    if (!adSet.getSettings().isNpiDetectionEnabled()) {
      return;
    }

    for (const source of this.sources) {
      if (!source.supports(adSet)) {
        continue;
      }

      const event = await this.eventManager.dispatch(
        Events.NPI_PRE_DETECT,
        new NpiPreDetectEvent(adSet, source),
      );

      if (event.isPropagationStopped()) {
        // if the event is stopped, we should skip this source
        continue;
      }

      const npiItems: PublicNpi[] = await (event.getSource().detect(adSet));

      for (const npiItem of npiItems) {
        if (!npiItem.encryptedNumber) {
          continue;
        }

        const npi = new Npi()
          .setEncryptedNumber(npiItem.encryptedNumber)
          .setSource(source.getType())
          .setDestination(npiItem.destination)
        ;

        const npiPostDetectEvent = await this.eventManager.dispatch(
          Events.NPI_POST_DETECT,
          new NpiPostDetectEvent(npi, adSet, source),
        );

        this.userContext.addNpi(npiPostDetectEvent.getNpi());
      }
    }
  }
}
